/*
  Cross-app session sync: Online Store theme <-> customer account app (Hydrogen).

  Both apps authenticate against the same Shopify auth server, but each holds
  its own session:

    - theme (bark.co)            -> Online Store customer session, the only
                                    thing that populates Liquid `customer`
    - customer app (customer.*)   -> Customer Account API token in its own
                                    HttpOnly cookie, which this domain can
                                    neither read nor write

  So the only way to establish the other app's session is a top-level trip
  through the auth server. Because the auth-server session already exists once
  either side has logged in, that trip is silent (no prompt, no UI). This file
  performs it once per browser session, in whichever direction is missing:

    theme logged in  -> bounce to `customer.bark.co/account/login?return_to=<here>`
    app logged in    -> bounce to the storefront login `?return_to=<here>`

  The `bark_signed_in_<env>` cookie is set on the customer app when it authorizes a customer
  (see the customer app's `app/lib/session-hint.ts`). It is a boolean hint only -- never a token.

  Each direction is guarded by its own one-shot cookie, set *before* navigating
  so a failed round trip can never loop. `layout/theme.liquid` clears all three
  on logout.

  Every config value, the per-environment cookie suffix included, arrives as a
  `data-*` attribute from `layout/theme.liquid` -- which is why this is plain
  JS and not a `.js.liquid` asset.

  Depends on `cookieFetcher`, `setHostCookie` and `deleteHostCookie` from
  `snippets/utils.liquid`. `layout/theme.liquid` renders that snippet inline
  near the top of <head>, so those globals exist by the time this file runs --
  even loaded `async`, since the browser cannot execute this before it has
  parsed the tag that requests it.
*/
(function () {
  'use strict';

  var script = document.currentScript;
  if (!script) return;

  /**
   * One suffix per environment (`_prd` / `_stg` / `_dev` / `_sbx`), resolved
   * from `shop.domain` in `layout/theme.liquid`.
   */
  var SUFFIX = script.dataset.cookieSuffix || '_sbx';

  /** Set by the customer app on `bark.co` when it authorizes a customer. */
  var SIGNED_IN_COOKIE = 'bark_signed_in' + SUFFIX;
  /**
   * One-shot guards, written with `setHostCookie`: host-only, so they never
   * reach a sibling environment, and session-scoped, so they reset with the
   * browser session -- roughly the lifetime of the sessions they track.
   *
   * CUSTOMER_WARM -- theme -> customer app bounce has been attempted.
   * THEME_WARM -- customer app -> theme bounce has been attempted.
   */
  var CUSTOMER_WARM_COOKIE = 'bark_customer_account_app_warm' + SUFFIX;
  var THEME_WARM_COOKIE = 'bark_theme_warm' + SUFFIX;

  /**
   * Paths where a redirect would interrupt something or is pointless: money
   * flows, Shopify-owned auth routes, and app/metaobject pages that hold their
   * own client-side state.
   */
  var SKIP_PATH_PREFIXES = [
    '/cart',
    '/checkout',
    '/checkouts',
    '/challenge',
    '/customer_authentication',
    '/account',
    '/apps',
    '/tools',
    '/orders',
    '/metaobjects',
  ];

  var config = script.dataset;
  var customerAccountOrigin = 'https://' + (config.customerAccountDomain || 'customer.bark.co')
  var storefrontLoginUrl = config.storefrontLoginUrl || '/customer_authentication/login';
  var themeLoggedIn = config.themeLoggedIn === 'true';

  function shouldSkipPath() {
    var pathname = location.pathname;
    if (pathname.indexOf('funnel') > -1) return true;
    for (var i = 0; i < SKIP_PATH_PREFIXES.length; i++) {
      if (pathname === SKIP_PATH_PREFIXES[i] || pathname.indexOf(SKIP_PATH_PREFIXES[i] + '/') === 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * `replace` keeps the bounce out of session history, so Back still works.
   */
  function bounce(url, base, param, value) {
    var target = new URL(url, base);
    target.searchParams.set(param, value);
    location.replace(target.toString());
  }

  if (!customerAccountOrigin) return;
  if (!navigator.cookieEnabled) return;
  // Only ever at the top level: an auth redirect inside an iframe (quickview,
  // Replo embeds, Ordergroove widgets) would be wrong and would be blocked.
  if (window.top !== window.self) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (shouldSkipPath()) return;

  if (themeLoggedIn) {
    // We know the theme is logged in and customer variable exists
    // Let's make the call the login the customer app
    if (!cookieFetcher(CUSTOMER_WARM_COOKIE)) {
      setHostCookie(CUSTOMER_WARM_COOKIE, '1');
      bounce('/account/login', customerAccountOrigin, 'return_to', location.href);
    }
    return;
  }

  // The SIGNED_IN_COOKIE does not exist, so let's reset the one-shot guards so the next login syncs from scratch;
  // Must be deleted from theme because they are host-only cookies on this domain.
  if (!cookieFetcher(SIGNED_IN_COOKIE)) {
    deleteHostCookie(CUSTOMER_WARM_COOKIE);
    deleteHostCookie(THEME_WARM_COOKIE);
    return;
  }

  // We know theme is not logged in but the SIGNED_IN_COOKIE exists, let's login to the theme
  if (!cookieFetcher(THEME_WARM_COOKIE)) {
    setHostCookie(THEME_WARM_COOKIE, '1');
    // Set this so the customer app does not warn the theme since we are logging in now
    setHostCookie(CUSTOMER_WARM_COOKIE, '1');
    bounce(
      storefrontLoginUrl,
      location.origin,
      'return_to',
      location.pathname + location.search,
    );
  }
})();
